import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $profileFeatureStore } from '@lib/store';
import { useCardFlow, useCurrencySymbol } from '@shared/hooks';
import { cn, FormatUtils, PaletteUtils } from '@shared/utils';
import { type CardDetailsResponse, type CardLimitsResponse, CardStatus, Frequency } from '@shared/api/rest/cards';
import { LimitRow, SidebarItem, SidebarRow } from '@widgets';
import { Button, Icon, Text } from '@shared/widgets';
import SettingIcon from '@assets/icons/setting.svg';
import SwapIcon from '@assets/icons/swap.svg';
import CancelIcon from '@assets/icons/cancel.svg';
import NotificationIcon from '@assets/icons/notification.svg';

interface ICardSidebarItemsProps {
  card: CardDetailsResponse;
  limits?: CardLimitsResponse;
  openBlockModal: (arg: boolean) => void;
  openPinModal: () => void;
  openReportModal: () => void;
  openLimitsModal: () => void;
  openEditRecipientsModal: () => void;
  openDetailsModal: () => void;
  showBlockModal: boolean;
}

export const CardSidebarItems = (props: ICardSidebarItemsProps) => {
  const { id } = useParams();

  const profileFeatureStore = useUnit($profileFeatureStore.store);

  const { t } = useTranslation('card');

  useEffect(() => {
    if (props.showBlockModal) props.openBlockModal(false);
  }, [props.card]);

  const blocked = useMemo(() => props.card.status === CardStatus.FROZEN || props.card.status === CardStatus.BLOCKED, [props.card]);
  useEffect(() => {
    props.openBlockModal(false);
  }, [blocked]);

  const cardFlow = useCardFlow(profileFeatureStore.data?.cardFlows, id ? parseInt(id) : undefined);
  const symbol = useCurrencySymbol(props.card.currency);
  const currentLimitValues = useMemo(() => {
    if (!props.limits) return null;
    if (props.limits.frequency === Frequency.DAILY) return props.limits.dayLimits;
    else if (props.limits.frequency === Frequency.CYCLE) return props.limits.cycleLimits;
    else if (props.limits.frequency === Frequency.YEAR) return props.limits.yearLimits;
    else return null;
  }, [props.limits]);
  return (
    <div
      className={cn(
        'w-80 bg-white p-6 dark:bg-soft-black',
        PaletteUtils.isVcfcPalette && ['rounded-lg border border-gray-black', 'max-md:mt-8 max-md:w-full'],
      )}
    >
      <SidebarItem title={t('sidebar.cardDetails.title')} icon={<Icon icon={<SettingIcon />} color='inherit' />}>
        <SidebarRow
          leftRowText={t('sidebar.cardDetails.fields.cardLabel')}
          rightRowText={props.card.cardLabel || t('sidebar.cardDetails.fields.none')}
        />
        <SidebarRow
          leftRowText={t('sidebar.cardDetails.fields.cardNumber')}
          rightRowText={props.card.cardNumber && FormatUtils.toSliceNumber(props.card.cardNumber)}
        />
        <SidebarRow
          leftRowText={t('sidebar.cardDetails.fields.cardCategory')}
          rightRowText={props.card.category || t('sidebar.cardDetails.fields.none')}
        />
        {cardFlow?.editCardLabel && (
          <Button className='mb-5' fullWidth color='outlined' onClick={props.openDetailsModal}>
            {t('buttons.editDetails', { ns: 'common' })}
          </Button>
        )}
      </SidebarItem>
      <SidebarItem title={t('sidebar.cardActions.title')} icon={<Icon icon={<SwapIcon />} color='inherit' />}>
        {cardFlow?.sendPinReminder && (
          <Button className='mb-5' fullWidth color='outlined' onClick={props.openPinModal}>
            {t('sidebar.cardActions.buttons.requestPin')}
          </Button>
        )}
        {cardFlow?.block && (
          <Button className='mb-5' fullWidth color='outlined' onClick={() => props.openBlockModal(true)}>
            {t(`sidebar.cardActions.buttons.${blocked ? 'unfreezeCard' : 'freezeCard'}`)}
          </Button>
        )}
        {cardFlow?.reportCardLostStolen && (
          <Button className='mb-5' fullWidth color='outlined' onClick={props.openReportModal}>
            {t('sidebar.cardActions.buttons.report')}
          </Button>
        )}
      </SidebarItem>
      {props.limits && currentLimitValues && (
        <SidebarItem title={t('sidebar.limits.title')} icon={<Icon icon={<CancelIcon />} color='inherit' />}>
          <SidebarRow
            leftRowText={t('sidebar.limits.fields.frequency')}
            rightRowText={t(`sidebar.limits.frequencies.${props.limits.frequency.toLowerCase()}`)}
          />
          <LimitRow
            label={t('sidebar.limits.fields.retailLimit')}
            currentAmount={currentLimitValues.retailAmount}
            limitAmount={props.limits.currentRetailAmount}
            limitNumber={props.limits.currentRetailNumber}
            currentNumber={currentLimitValues.retailNumber}
            symbol={symbol}
          />
          <LimitRow
            label={t('sidebar.limits.fields.cashLimit')}
            limitAmount={props.limits.currentCashAmount}
            currentAmount={currentLimitValues.cashAmount}
            limitNumber={props.limits.currentCashNumber}
            currentNumber={currentLimitValues.cashNumber}
            symbol={symbol}
          />
          <LimitRow
            label={t('sidebar.limits.fields.totalLimit')}
            limitAmount={props.limits.currentOTCAmount}
            currentAmount={currentLimitValues.otcAmount}
            limitNumber={props.limits.currentOTCNumber}
            currentNumber={currentLimitValues.otcNumber}
            symbol={symbol}
          />
          {cardFlow?.updateCardLimit && (
            <Button className='mb-5' fullWidth color='outlined' disabled={props.card.status === CardStatus.BLOCKED} onClick={props.openLimitsModal}>
              {t('sidebar.limits.buttons.requestNewLimits')}
            </Button>
          )}
        </SidebarItem>
      )}
      <SidebarItem
        title={t('sidebar.alertsNotifications.title')}
        icon={<Icon icon={<NotificationIcon />} color='inherit' />}
        isHidden={!cardFlow?.cardAlertsSettings.declinedAuths}
      >
        <Text className='mb-5' size='sm'>
          {t('sidebar.alertsNotifications.subtitle')}
        </Text>
        {props.card.declinedAuthorizationSubscription?.recipientDetails.map((item, idx) => (
          <SidebarRow key={idx} leftRowText={item.firstName || ''} rightRowText={item.email} />
        ))}
        <Button className='mb-5' fullWidth color='outlined' onClick={props.openEditRecipientsModal}>
          {t('sidebar.alertsNotifications.buttons.editRecipients')}
        </Button>
      </SidebarItem>
    </div>
  );
};
